import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';

/**
 * PaymentReturnPage
 * Cashfree redirects here after payment completes (success or failure).
 * URL params: ?order_id=...&order_token=...&txStatus=...&type=sub|order
 */
export default function PaymentReturnPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'failed'
    const [message, setMessage] = useState('Verifying your payment securely...');

    useEffect(() => {
        const verify = async () => {
            const cashfreeOrderId = searchParams.get('order_id');
            const paymentType = searchParams.get('type') || 'sub'; // 'sub' or 'order'

            if (!cashfreeOrderId) {
                setStatus('failed');
                setMessage('Invalid payment session. Please contact support.');
                return;
            }

            try {
                // Call our secure backend to verify
                const { data: verifyData, error: verifyErr } = await supabase.functions.invoke('verify-cashfree-payment', {
                    body: { orderId: cashfreeOrderId }
                });

                if (verifyErr || !verifyData?.verified) {
                    setStatus('failed');
                    setMessage('Payment could not be verified. If you were charged, please contact support.');
                    setTimeout(() => navigate('/subscription'), 4000);
                    return;
                }

                // Payment verified — now update the database
                const { data: { user } } = await supabase.auth.getUser();

                if (paymentType === 'sub' && user) {
                    // B2B: Activate the restaurant plan
                    // The plan id and details were stored in the order_id: sub_{userId}_{planId}_{billingCycle}_{couponId}_{addons}_{timestamp}
                    const parts = cashfreeOrderId.split('_');
                    const planId = parts[2] || 'starter';
                    const billingCycle = parts[3] || 'monthly';
                    const couponId = parts[4] || 'NONE';
                    const addonsPart = parts[5] || 'NONE';

                    const now = new Date();
                    const expiresAt = new Date(now);
                    let addMonths = 1;
                    if (billingCycle === '3month') addMonths = 3;
                    if (billingCycle === '6month') addMonths = 6;
                    expiresAt.setMonth(expiresAt.getMonth() + addMonths);

                    // 1. Update Restaurant Plan
                    const { data: restData } = await supabase.from('restaurants').update({
                        plan: planId,
                        cashfree_order_id: cashfreeOrderId,
                        cashfree_payment_id: verifyData.payment_id || cashfreeOrderId,
                        plan_activated_at: now.toISOString(),
                        plan_expires_at: expiresAt.toISOString(),
                    }).eq('owner_id', user.id).select('id').single();

                    const restaurantId = restData?.id;

                    // 2. Process Add-ons
                    if (restaurantId && addonsPart !== 'NONE') {
                        const addonIds = addonsPart.split('|');
                        const addonInserts = addonIds.map(id => ({
                            restaurant_id: restaurantId,
                            addon_id: id,
                            activated_at: now.toISOString(),
                            expires_at: expiresAt.toISOString(),
                            is_active: true
                        }));
                        // We use upsert or delete-insert to avoid duplicates if re-purchasing
                        await supabase.from('restaurant_addons').delete().eq('restaurant_id', restaurantId).in('addon_id', addonIds);
                        await supabase.from('restaurant_addons').insert(addonInserts);
                    }

                    // 3. Increment Coupon Usage
                    if (couponId !== 'NONE') {
                        // Use RPC or a simple select-update for now
                        const { data: cpn } = await supabase.from('plan_coupons').select('current_uses').eq('id', couponId).single();
                        if (cpn) {
                            await supabase.from('plan_coupons').update({ current_uses: (cpn.current_uses || 0) + 1 }).eq('id', couponId);
                        }
                    }

                    setStatus('success');
                    setMessage('🎉 Payment successful! Your plan is now active.');
                    // Hard redirect so ProtectedRoute/PlanGate re-reads the fresh plan from DB
                    setTimeout(() => { window.location.href = '/dashboard'; }, 1500);
                } else {
                    // B2C: Mark the food order as paid
                    // cashfreeOrderId format: ord_{full-uuid}_{timestamp}
                    // Extract the UUID between first 'ord_' and last '_timestamp'
                    const withoutPrefix = cashfreeOrderId.replace(/^ord_/, '');
                    const lastUnderscoreIdx = withoutPrefix.lastIndexOf('_');
                    const dbOrderId = lastUnderscoreIdx > 0 ? withoutPrefix.substring(0, lastUnderscoreIdx) : withoutPrefix;
                    if (dbOrderId) {
                        await supabase.from('orders').update({
                            payment_status: 'paid',
                            cashfree_order_id: cashfreeOrderId,
                            cashfree_payment_id: verifyData.payment_id || cashfreeOrderId,
                        }).eq('id', dbOrderId);
                    }
                    setStatus('success');
                    setMessage('🎉 Payment successful! Your order has been placed.');
                    setTimeout(() => navigate('/'), 2500);
                }

            } catch (err) {
                console.error('[PaymentReturn] Error:', err);
                setStatus('failed');
                setMessage(err.message || 'An unexpected error occurred.');
                setTimeout(() => navigate('/subscription'), 4000);
            }
        };

        verify();
    }, [searchParams, navigate]);

    return (
        <div className="min-h-screen bg-[#FDFCF7] flex flex-col items-center justify-center font-display px-6">
            <div className="max-w-md w-full bg-white rounded-[32px] shadow-xl p-10 text-center">
                {status === 'verifying' && (
                    <>
                        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-6" />
                        <h1 className="text-2xl font-black text-slate-900 mb-2">Verifying Payment</h1>
                        <p className="text-slate-500 text-sm">Please wait while we confirm your payment securely...</p>
                    </>
                )}
                {status === 'success' && (
                    <>
                        <div className="text-6xl mb-6">✅</div>
                        <h1 className="text-2xl font-black text-slate-900 mb-2">Payment Confirmed!</h1>
                        <p className="text-slate-500 text-sm">{message}</p>
                        <p className="text-slate-400 text-xs mt-3">Redirecting you now...</p>
                    </>
                )}
                {status === 'failed' && (
                    <>
                        <div className="text-6xl mb-6">❌</div>
                        <h1 className="text-2xl font-black text-slate-900 mb-2">Payment Failed</h1>
                        <p className="text-slate-500 text-sm">{message}</p>
                        <p className="text-slate-400 text-xs mt-3">Redirecting you back...</p>
                    </>
                )}
            </div>
        </div>
    );
}
